import React, { useState, useCallback, useEffect } from 'react';
import { GoogleMap, useJsApiLoader, Marker, InfoWindow, Circle } from '@react-google-maps/api';
import { format } from 'date-fns';
import { getGoogleMapsConfig } from '../../utils/mapsConfig';

// Map container style
const containerStyle = {
  width: '100%',
  height: '100%'
};

// Default center (New York City)
const defaultCenter = {
  lat: 40.7128,
  lng: -74.006
};

// Map options
const mapOptions = {
  disableDefaultUI: true,
  zoomControl: true,
  mapTypeControl: true,
  streetViewControl: false,
  mapTypeId: 'roadmap'
};

const VehicleMap = ({ vehicles = [], geofences = [], center = null, zoom = 12 }) => {
  const [map, setMap] = useState(null);
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [mapCenter, setMapCenter] = useState(center || defaultCenter);
  
  // Load Google Maps script with shared config
  const { isLoaded, loadError } = useJsApiLoader(getGoogleMapsConfig());
  
  // Fit map to show all vehicles
  const fitBoundsToVehicles = useCallback(() => {
    if (map && vehicles.length > 0) {
      const bounds = new window.google.maps.LatLngBounds();
      
      vehicles.forEach(vehicle => {
        if (vehicle.currentPosition) {
          bounds.extend({
            lat: vehicle.currentPosition.lat,
            lng: vehicle.currentPosition.lng
          });
        }
      });
      
      map.fitBounds(bounds);
      
      // If only one vehicle, zoom in a bit more
      if (vehicles.length === 1) {
        map.setZoom(14);
      }
    }
  }, [map, vehicles]);
  
  // Effect to fit bounds when vehicles or map changes
  useEffect(() => {
    if (isLoaded && map) {
      if (vehicles.length > 0) {
        fitBoundsToVehicles();
      } else if (center) {
        map.setCenter(center);
        map.setZoom(zoom);
      }
    }
  }, [isLoaded, map, vehicles, center, zoom, fitBoundsToVehicles]);
  
  // Handle map load
  const onLoad = useCallback((map) => {
    setMap(map);
  }, []);
  
  // Handle map unmount
  const onUnmount = useCallback(() => {
    setMap(null);
  }, []);
  
  // Get marker icon based on vehicle status
  const getMarkerIcon = (status) => {
    let iconColor;
    
    switch (status) {
      case 'active':
        iconColor = '#22c55e'; // Green-500 from Tailwind
        break;
      case 'idle':
        iconColor = '#eab308'; // Yellow-500 from Tailwind
        break;
      case 'stopped':
        iconColor = '#ef4444'; // Red-500 from Tailwind
        break;
      case 'offline':
      default:
        iconColor = '#6b7280'; // Gray-500 from Tailwind
    }
    
    return {
      path: window.google.maps.SymbolPath.FORWARD_CLOSED_ARROW,
      scale: 6, // Slightly larger
      fillColor: iconColor,
      fillOpacity: 1,
      strokeWeight: 1,
      strokeColor: '#ffffff', // White outline for better visibility
      rotation: 0 // Could use vehicle.heading here if available
    };
  };
  
  // Get status text
  const getStatusText = (status) => {
    switch (status) {
      case 'active':
        return 'Active';
      case 'idle':
        return 'Idle';
      case 'stopped':
        return 'Stopped';
      case 'offline':
      default:
        return 'Offline';
    }
  };
  
  // Render loading message
  if (loadError) {
    return <div className="text-ui-error p-4">Error loading maps</div>;
  }
  
  if (!isLoaded) {
    return <div className="flex justify-center items-center h-full">Loading maps...</div>;
  }
  
  return (
    <GoogleMap
      mapContainerStyle={containerStyle}
      center={mapCenter}
      zoom={zoom}
      options={mapOptions}
      onLoad={onLoad}
      onUnmount={onUnmount}
    >
      {/* Geofence circles */}
      {geofences.map(geofence => {
        if (geofence.type === 'circle' && geofence.center && geofence.radius) {
          return (
            <Circle
              key={geofence.id}
              center={geofence.center}
              radius={geofence.radius}
              options={{
                fillColor: geofence.color || '#1A5F7A',
                fillOpacity: 0.2,
                strokeColor: geofence.color || '#1A5F7A',
                strokeOpacity: 0.8,
                strokeWeight: 2
              }}
            />
          );
        }
        return null;
      })}
      
      {/* Vehicle markers */}
      {vehicles.map(vehicle => {
        if (!vehicle.currentPosition) return null;
        
        return (
          <Marker
            key={vehicle.id}
            position={{
              lat: vehicle.currentPosition.lat,
              lng: vehicle.currentPosition.lng
            }}
            onClick={() => setSelectedVehicle(vehicle)}
            icon={getMarkerIcon(vehicle.status)}
            title={vehicle.name}
          />
        );
      })}
      
      {/* Selected vehicle info window */}
      {selectedVehicle && selectedVehicle.currentPosition && (
        <InfoWindow
          position={{
            lat: selectedVehicle.currentPosition.lat,
            lng: selectedVehicle.currentPosition.lng
          }}
          onCloseClick={() => setSelectedVehicle(null)}
        >
          <div className="max-w-xs">
            <h3 className="font-semibold text-lg">{selectedVehicle.name}</h3>
            <p className="text-sm text-gray-500">{selectedVehicle.registration}</p>
            
            <div className="mt-2 space-y-1">
              <p className="text-sm">
                <span className="font-medium">Status:</span>{' '}
                <span className={`
                  ${selectedVehicle.status === 'active' ? 'text-ui-success' : ''}
                  ${selectedVehicle.status === 'idle' ? 'text-ui-warning' : ''}
                  ${selectedVehicle.status === 'stopped' ? 'text-ui-error' : ''}
                  ${selectedVehicle.status === 'offline' ? 'text-gray-500' : ''}
                `}>
                  {getStatusText(selectedVehicle.status)}
                </span>
              </p>
              
              {selectedVehicle.currentSpeed !== undefined && (
                <p className="text-sm">
                  <span className="font-medium">Speed:</span>{' '}
                  {selectedVehicle.currentSpeed} mph
                </p>
              )}
              
              {selectedVehicle.lastUpdate && (
                <p className="text-sm">
                  <span className="font-medium">Last update:</span>{' '}
                  {format(new Date(selectedVehicle.lastUpdate), 'h:mm:ss a')}
                </p>
              )}
            </div>
            
            <div className="mt-2">
              <a 
                href={`/vehicles/${selectedVehicle.id}`}
                className="text-sm text-secondary hover:underline"
              >
                View details
              </a>
            </div>
          </div>
        </InfoWindow>
      )}
    </GoogleMap>
  );
};

export default VehicleMap;